import { notifyManager, useQueryClient } from '@tanstack/react-query';
import { API_QUERY_KEYS } from 'consts';
import { getDocument, getDocumentsQueue } from 'entities/document/api/requests';
import type {
  DocumentCacheEntry,
  DocumentCacheFetchParams,
  DocumentCacheInvalidateParams,
} from './types';

/**
 * Composable hook for managing individual document cache entries.
 *
 * Handles `[document, { documentId }]` and `[documentsQueue, { documentId }]` queries.
 * Does NOT manage collections, lists, or packages — use dedicated managers for those.
 */
export const useDocumentCacheManager = () => {
  const queryClient = useQueryClient();

  /**
   * Directly set document data in cache.
   * Use when API response already contains fresh document data.
   */
  const setDocuments = (entries: DocumentCacheEntry[]) => {
    notifyManager.batch(() => {
      entries.forEach(({ documentId, data }) => {
        queryClient.setQueryData(
          [API_QUERY_KEYS.document, { documentId }],
          data,
        );
      });
    });
  };

  /**
   * Fetch fresh document data from API and set it in cache.
   * All documents are fetched in parallel, then cache is updated atomically.
   */
  const fetchDocuments = async ({
    documentIds,
    includeQueue = true,
  }: DocumentCacheFetchParams) => {
    const toUpdate: { queryKey: readonly unknown[]; queryData: any }[] = [];

    const documentsData = await Promise.all(
      documentIds.map(async documentId => ({
        queryKey: [API_QUERY_KEYS.document, { documentId }] as const,
        queryData: await getDocument({ documentId }),
      })),
    );
    toUpdate.push(...documentsData);

    if (includeQueue) {
      const queuesData = await Promise.all(
        documentIds.map(async documentId => ({
          queryKey: [API_QUERY_KEYS.documentsQueue, { documentId }] as const,
          queryData: await getDocumentsQueue({ documentId }),
        })),
      );
      toUpdate.push(...queuesData);
    }

    notifyManager.batch(() => {
      toUpdate.forEach(({ queryKey, queryData }) => {
        queryClient.setQueryData(queryKey, queryData);
      });
    });
  };

  /**
   * Mark documents as stale. They will refetch on next access.
   * Use when API does not return fresh document data after mutation.
   */
  const invalidateDocuments = ({
    documentIds,
    includeQueue = true,
    includeQueueGroup = true,
  }: DocumentCacheInvalidateParams) => {
    notifyManager.batch(() => {
      documentIds.forEach(documentId => {
        queryClient.invalidateQueries([
          API_QUERY_KEYS.document,
          { documentId },
        ]);
      });

      if (includeQueue) {
        documentIds.forEach(documentId => {
          queryClient.invalidateQueries([
            API_QUERY_KEYS.documentsQueue,
            { documentId },
          ]);
        });
      }

      if (includeQueueGroup) {
        documentIds.forEach(documentId => {
          queryClient.invalidateQueries([
            API_QUERY_KEYS.documentsQueueGroups,
            { documentId },
          ]);
        });
      }
    });
  };

  return { setDocuments, fetchDocuments, invalidateDocuments };
};
